import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Product, ProductOverview, ProductType } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { ListOfProducts } from "../../forms/ListOfProducts/ListOfProducts";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Utility } from "../../core/Utility";
import { useApplicationContext } from "../../application/ApplicationContext";
import { activeUser } from "../../services/AuthService";
import {
  getAllDocuments,
  syncStockBalanceYear,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import "./ProductOverviewPage.scss";

export const ProductOverviewPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    const from = new Date(businessYear, 0, 1); // 1.1. poslovne godine
    const to =
      businessYear === today.getFullYear()
        ? today // tekuća godina -> danas
        : new Date(businessYear, 11, 31); // druga godina -> 31.12.

    return { from, to };
  };
  const defaultRange = getDefaultRangeForBusinessYear();

  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [productType, setProductType] = useState<string>("Svu robu");
  const [dateTo, setDateTo] = useState<Date>(defaultRange.to);
  const [processing, setProcessing] = useState(false);
  const [warehousesLocal, setWarehousesLocal] = useState<any[]>([]);
  const [productsLocal, setProductsLocal] = useState<any[]>([]);

  const [mercantileOverview, setMercantileOverview] = useState<
    ProductOverview[]
  >([]);
  const [repromaterialOverview, setRepromaterialOverview] = useState<
    ProductOverview[]
  >([]);

  const [selectedProduct, setSelectedProduct] = useState<ProductOverview>();
  const [listOfProductsVisible, setListOfProductsVisible] =
    useState<boolean>(false);
  const [selectedProductList, setSelectedProductList] = useState<
    {
      productName: string;
      warehouseName: string;
      productOverview: number;
    }[]
  >([]);
  const loadSeqRef = useRef(0);

  const normalize = (v: any) =>
    String(v ?? "")
      .trim()
      .toLowerCase();

  const resolveProduct = (token: any, sourceProducts = productsLocal) => {
    if (token === null || token === undefined) return undefined;
    const raw = String(token).trim();
    if (!raw) return undefined;

    const byId = sourceProducts.find((p) => p?.id?.toString() === raw);
    if (byId) return byId;

    const byCode = sourceProducts.find(
      (p) => String(p?.code ?? "").trim() === raw,
    );
    if (byCode) return byCode;

    const n = normalize(raw);
    const byName = sourceProducts.find((p) => normalize(p?.name) === n);
    return byName;
  };

  const refreshOverview = async (opts?: {
    products?: any[];
    warehouses?: any[];
  }) => {
    const seq = ++loadSeqRef.current;
    setProcessing(true);

    const productsSrc = opts?.products ?? productsLocal;
    const warehousesSrc = opts?.warehouses ?? warehousesLocal;

    if (!productsSrc?.length || !warehousesSrc?.length) {
      if (seq === loadSeqRef.current) setProcessing(false);
      return;
    }

    const [merc, repro] = await Promise.all([
      formatMercantileOverviewData(productsSrc, warehousesSrc),
      formatRepromaterialOverviewData(productsSrc, warehousesSrc),
    ]);

    // ako se u međuvremenu desio novi refresh, ignoriši ovaj
    if (seq !== loadSeqRef.current) return;

    setMercantileOverview(merc);
    setRepromaterialOverview(repro);
    setProcessing(false);
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);

      const [whResp, prResp] = await Promise.all([
        getAllWarehouses(),
        getAllProducts(),
      ]);

      const wh = whResp?.data?.result ?? [];
      const pr = prResp?.data?.result ?? [];

      setWarehousesLocal(wh);
      setProductsLocal(pr);

      await refreshOverview({ warehouses: wh, products: pr });
    };
    run();

    return () => {
      loadSeqRef.current++;
    };
  }, []);

  useEffect(() => {
    const { to } = getDefaultRangeForBusinessYear();
    setDateTo(to);
  }, [businessYear]);

  useEffect(() => {
    const run = async () => {
      if (!productsLocal.length || !warehousesLocal.length) return;
      await refreshOverview();
    };
    run();
  }, [
    selectedWarehouse,
    dateTo,
    productType,
    businessYear,
    productsLocal.length,
    warehousesLocal.length,
  ]);

  useEffect(() => {
    const buildList = async () => {
      if (!selectedProduct) {
        setSelectedProductList([]);
        return;
      }

      const dateFromValue = new Date(businessYear, 0, 1);
      const dateToValue = dateTo;
      const dateFromMs = dateFromValue.getTime();
      const dateToMs = dateToValue.getTime() + 1000 * 60 * 60 * 24;

      const isMercantile = selectedProduct.type === ProductType.MercantileGoods;

      const map = new Map<string, number>();

      if (isMercantile) {
        const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
          await Promise.all([
            getAllDocuments(true, true, dateFromValue, dateToValue),
            getAllDocuments(true, false, dateFromValue, dateToValue),
          ]);

        const inputs = inputsResp.data.result;
        const outputs = outputsResp.data.result;

        const processDoc = (doc: any, sign: 1 | -1) => {
          if (doc?.isDocumentFinished !== true) return;
          const ts = new Date(doc.date).getTime();
          if (ts < dateFromMs || ts > dateToMs) return;

          const productFromList =
            productsLocal.find((p) =>
              doc.productId
                ? p.id?.toString() === doc.productId.toString()
                : p.name === doc.productName,
            ) || undefined;

          const prodId =
            productFromList?.id ?? doc.productId ?? doc.productName;

          if (
            selectedProduct.product?.id &&
            selectedProduct.product.id.toString() !== prodId.toString()
          ) {
            return;
          }

          const whName = doc.warehouseName as string;
          const netto = doc.netto ?? 0;
          const current = map.get(whName) ?? 0;
          map.set(whName, current + sign * netto);
        };

        for (const d of inputs) processDoc(d, 1);
        for (const d of outputs) processDoc(d, -1);
      } else {
        const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
          await Promise.all([
            getAllDocuments(false, true, dateFromValue, dateToValue),
            getAllDocuments(false, false, dateFromValue, dateToValue),
          ]);
        const inputs = inputsResp.data.result;
        const outputs = outputsResp.data.result;

        const processDoc = (doc: any, sign: 1 | -1) => {
          if (isVirtualWarehouseDoc(doc)) return;
          if (doc?.isDocumentFinished !== true) return;
          const ts = new Date(doc.date).getTime();
          if (ts < dateFromMs || ts > dateToMs) return;

          const productIds = (doc.reproProducts ?? "")
            .split(";")
            .map((x: string) => x.trim())
            .filter((x: string) => x !== "");
          const amounts = (doc.reproAmounts ?? "")
            .split(";")
            .map((x: string) => x.trim())
            .filter((x: string) => x !== "");

          productIds.forEach((token: string, idx: number) => {
            const rawAmount = amounts[idx] ?? "0";
            const amount = parseFloat(rawAmount) || 0;
            if (!amount) return;

            const resolved = resolveProduct(token);
            const selectedId = selectedProduct.product?.id?.toString();
            const selectedCode = String(
              selectedProduct.product?.code ?? "",
            ).trim();
            const selectedName = normalize(selectedProduct.product?.name);
            const matchesSelected =
              (selectedId && resolved?.id?.toString() === selectedId) ||
              (selectedCode &&
                String(resolved?.code ?? "").trim() === selectedCode) ||
              (selectedName && normalize(resolved?.name) === selectedName);
            if (!matchesSelected) return;

            const whName = doc.warehouseName as string;
            const current = map.get(whName) ?? 0;
            map.set(whName, current + sign * amount);
          });
        };

        for (const d of inputs) processDoc(d, 1);
        for (const d of outputs) processDoc(d, -1);
      }

      const productName = selectedProduct.name;
      const list = Array.from(map.entries())
        .map(([warehouseName, productOverview]) => ({
          productName,
          warehouseName,
          productOverview,
        }))
        .filter((item) => Math.abs(item.productOverview) > 0.0005)
        .sort((a, b) => a.warehouseName.localeCompare(b.warehouseName, "sr"));

      setSelectedProductList(list);
    };

    buildList();
  }, [selectedProduct, businessYear, dateTo]);

  const formatMercantileOverviewData = async (
    productsSrc = productsLocal,
    warehousesSrc = warehousesLocal,
  ): Promise<ProductOverview[]> => {
    const warehouse = warehousesSrc.find(
      (item) => item.id == selectedWarehouse,
    );
    const warehouseName = warehouse?.name || undefined;

    const dateFromValue = new Date(businessYear, 0, 1);
    const dateToValue = dateTo;
    const dateFromMs = dateFromValue.getTime();
    const dateToMs = dateToValue.getTime() + 1000 * 60 * 60 * 24;

    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFromValue, dateToValue), // merkantila - prijemnice
        getAllDocuments(true, false, dateFromValue, dateToValue), // merkantila - otpremnice
      ]);

    const mercantileInputs = inputsResp.data.result;
    const mercantileOutputs = outputsResp.data.result;

    const map = new Map<string, ProductOverview>();

    const processDoc = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true) return;

      const ts = new Date(doc.date).getTime();
      if (ts < dateFromMs || ts > dateToMs) return;

      if (warehouseName && doc.warehouseName !== warehouseName) return;

      const productFromList =
        resolveProduct(doc.productId ?? doc.productName, productsSrc) ||
        undefined;

      const productId = productFromList?.id ?? doc.productId ?? doc.productName;
      const name = productFromList?.name ?? (doc.productName as string) ?? "";
      const code = productFromList?.code ?? (doc.productCode as string) ?? "";
      const mark = productFromList?.measuringUnit ?? doc.measuringUnit ?? "";

      const key = productId.toString();
      const netto = doc.netto ?? 0;

      let item = map.get(key);
      if (!item) {
        const productObj: Product =
          productFromList ??
          ({
            id: productId,
            name,
            code,
            type: ProductType.MercantileGoods,
          } as Product);

        item = {
          id: productId,
          code,
          name,
          year: businessYear,
          type: ProductType.MercantileGoods,
          product: productObj,
          mark,
          amount: 0,
        };
        map.set(key, item);
      }

      item.amount = (item.amount ?? 0) + sign * netto;
    };

    for (const mercantileInput of mercantileInputs) {
      processDoc(mercantileInput, 1);
    }

    for (const mercantileOutput of mercantileOutputs) {
      processDoc(mercantileOutput, -1);
    }

    const result = Array.from(map.values()).filter(
      (item) => Math.abs(item.amount ?? 0) > 0.0005,
    );

    result.sort((a, b) => a.name.localeCompare(b.name, "sr"));

    return result;
  };

  const isVirtualWarehouseDoc = (doc: any) => {
    if (doc?.isWarehouseVirtual === true) return true;
    const wn = String(doc?.warehouseName ?? "").toLowerCase();
    return wn.includes("virtuel") || wn.includes("virtual");
  };

  const formatRepromaterialOverviewData = async (
    productsSrc = productsLocal,
    warehousesSrc = warehousesLocal,
  ): Promise<ProductOverview[]> => {
    const warehouse = warehousesSrc.find(
      (item) => item.id === selectedWarehouse,
    );
    const warehouseName = warehouse?.name || undefined;

    const dateFromValue = new Date(businessYear, 0, 1);
    const dateToValue = dateTo;
    const dateFromMs = dateFromValue.getTime();
    const dateToMs = dateToValue.getTime() + 1000 * 60 * 60 * 24;

    const [reproInputsResp, reproOutputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(false, true, dateFromValue, dateToValue),
        getAllDocuments(false, false, dateFromValue, dateToValue),
      ]);

    const repromaterialInputs = reproInputsResp.data.result;
    const repromaterialOutputs = reproOutputsResp.data.result;

    const map = new Map<string, ProductOverview>();

    const processDoc = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true) return;

      const ts = new Date(doc.date).getTime();
      if (ts < dateFromMs || ts > dateToMs) return;

      if (warehouseName && doc.warehouseName !== warehouseName) return;

      if (isVirtualWarehouseDoc(doc)) return;

      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      productIds.forEach((token: string, idx: number) => {
        const rawAmount = amounts[idx] ?? "0";
        const amount = parseFloat(rawAmount) || 0;
        if (!amount) return;

        const productFromList = resolveProduct(token, productsSrc);

        const id = productFromList?.id ?? token;
        const code = productFromList?.code ?? ""; // bitno
        const name = productFromList?.name ?? String(token); // fallback
        const mark = productFromList?.measuringUnit ?? "Kom";

        const key = id.toString();

        let item = map.get(key);
        if (!item) {
          const productObj: Product =
            productFromList ??
            ({
              id,
              name,
              code,
              type: ProductType.RawMaterial,
            } as Product);

          item = {
            id,
            code,
            name,
            year: businessYear,
            type: ProductType.RawMaterial,
            product: productObj,
            mark,
            amount: 0,
          };
          map.set(key, item);
        }

        item.amount = (item.amount ?? 0) + sign * amount;
      });
    };

    for (const d of repromaterialInputs) processDoc(d, 1);
    for (const d of repromaterialOutputs) processDoc(d, -1);

    const result = Array.from(map.values()).filter(
      (item) => Math.abs(item.amount ?? 0) > 0.0005,
    );

    result.sort((a, b) => a.name.localeCompare(b.name, "sr"));
    return result;
  };

  const onSelectedWarehouseChange = (property: string, value: string) => {
    setSelectedWarehouse(value);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const onProductTypeChange = (property: string, value: string) => {
    setProductType(value);
  };

  const renderMercantileHeader = () => {
    return (
      <div className="mercantile-header">
        <span>{t("MERKANTILNA ROBA")}</span>
      </div>
    );
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const typeOfProduct = (rowData: any) => {
    return rowData.type == ProductType.MercantileGoods
      ? t("Merkantila")
      : t("Repromaterijal");
  };

  const productAmount = (rowData: any) => {
    return Utility.addThousandSeparator(rowData.amount);
  };

  const categoryOfProduct = (rowData: any) => {
    return productsLocal.find((item) => item.id === rowData.product.id)
      ?.category?.name;
  };

  const markOfProduct = (rowData: any) => {
    return productsLocal.find((item) => item.id === rowData.product.id)
      ?.measuringUnit;
  };

  const amountOfProduct = (rowData: any) => {
    return !Number.isInteger(rowData.amount)
      ? rowData.amount.toFixed(3) == 0 ||
        rowData.amount.toFixed(3) == 0.001 ||
        rowData.amount.toFixed(3) == -0.001
        ? "0"
        : rowData.amount.toFixed(3)
      : rowData.amount.toFixed(0);
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const exportToExcel = () => {
    const excelMercantileObject = mercantileOverview.map(
      (item: ProductOverview) => {
        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          Tip: "Merkantila",
          VrstaRobe: "/",
          Oznaka:
            productsLocal.find(
              (oneProduct) => oneProduct.id === item?.product?.id,
            )?.measuringUnit ?? "",
          Kolicina: item.amount ?? "",
        };
      },
    );
    const excelRepromaterialObject = repromaterialOverview.map(
      (item: ProductOverview) => {
        const amount = item.amount ?? 0;
        const formattedAmount = Number.isInteger(amount)
          ? amount
          : Math.abs(amount) < 0.001
            ? 0
            : amount;
        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          Tip: "Repromaterijal",
          VrstaRobe:
            productsLocal.find(
              (oneProduct) => oneProduct.id === item?.product?.id,
            )?.category?.name ?? "",
          Oznaka:
            productsLocal.find(
              (oneProduct) => oneProduct.id === item?.product?.id,
            )?.measuringUnit ?? "",
          Kolicina: formattedAmount,
        };
      },
    );
    let excelObject: any[] = [];
    const emptyRow = {
      SifraRobe: "",
      Naziv: "",
      Tip: "",
      VrstaRobe: "",
      Oznaka: "",
      Kolicina: "",
    };
    if (productType === "Svu robu") {
      excelObject = [
        emptyRow,
        ...excelMercantileObject,
        emptyRow,
        ...excelRepromaterialObject,
      ];
    } else if (productType === "Merkantila") {
      excelObject = [emptyRow, ...excelMercantileObject];
    } else if (productType === "Repromaterijal") {
      excelObject = [emptyRow, ...excelRepromaterialObject];
    }
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          const cellAddress = xlsx.utils.encode_cell({ r: i, c: 5 }); // Kolona F (Kolicina)
          if (
            worksheet[cellAddress] &&
            typeof worksheet[cellAddress].v === "number"
          ) {
            worksheet[cellAddress].t = "n"; // Numerički tip
            // Dinamički format u zavisnosti od toga da li je ceo broj
            if (Number.isInteger(worksheet[cellAddress].v)) {
              worksheet[cellAddress].z = "0"; // Format za cele brojeve
            } else {
              worksheet[cellAddress].z = "0.00"; // Format za decimale
            }
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Stanje robe na dan ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const handleSyncStockBalance = async () => {
    if (processing) return;

    const confirmed = window.confirm(
      "Da li ste sigurni da želite sinhronizaciju stanja za izabranu poslovnu godinu?",
    );
    if (!confirmed) return;

    try {
      setProcessing(true);

      await syncStockBalanceYear(businessYear);

      // posle sync-a osveži prikaz na stranici
      setMercantileOverview([]);
      setRepromaterialOverview([]);

      await Promise.all([
        formatMercantileOverviewData(),
        formatRepromaterialOverviewData(),
      ]);
    } catch (err) {
      console.error("Sinhronizacija stanja neuspešna:", err);
      alert(t("Greška pri sinhronizaciji stanja"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">{t("Stanje robe za")}</span>
            <DropdownInput
              id="productType"
              value={productType}
              onValueChange={onProductTypeChange}
              options={[
                { label: t("Svu robu"), value: "Svu robu" },
                { label: t("Merkantila"), value: "Merkantila" },
                { label: t("Repromaterijal"), value: "Repromaterijal" },
              ]}
              labelText=""
              placeholderText=""
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("na dan")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("u magacinu")}</span>
            <DropdownInput
              id="selectedWarehouse"
              value={selectedWarehouse}
              onValueChange={onSelectedWarehouseChange}
              options={warehousesLocal.filter((item) => item.virtual !== true)}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
            />
            {activeUser?.role?.name === RoleType.Superadmin && (
              <ButtonInput
                addStyle={{
                  border: "none",
                  background: "#ffffff",
                  color: "#6C757D",
                  margin: "0 0 0 1rem",
                }}
                labelText={t("Sinhronizacija stanja")}
                onButtonClick={handleSyncStockBalance}
              />
            )}
          </div>
        </div>
        {processing ? (
          <ProgressSpinner
            style={{ width: "100px", height: "100px", color: "green" }}
            strokeWidth="4"
            animationDuration=".8s"
          />
        ) : (
          (productType === "Svu robu" || productType === "Merkantila") && (
            <div className="table-body">
              <DataTable
                header={renderMercantileHeader}
                value={mercantileOverview}
                selectionMode="single"
                selection={selectedProduct}
                onSelectionChange={(e) => {
                  setSelectedProductList([]);
                  setSelectedProduct(e.value);
                }}
                onRowSelect={() => setListOfProductsVisible(true)}
                dataKey="code"
                metaKeySelection={false}
                showGridlines
                stripedRows
              >
                <Column field="code" header={`${t("Šifra robe")}`} />
                <Column field="name" header={`${t("Naziv proizvoda")}`} />
                <Column body={typeOfProduct} header={`${t("Tip robe")}`} />
                <Column field="mark" header={`${t("Oznaka")}`} />
                <Column body={productAmount} header={`${t("Količina")}`} />
              </DataTable>
            </div>
          )
        )}
        {!processing &&
          (productType === "Svu robu" || productType === "Repromaterijal") && (
            <div className="table-body-repromaterial">
              <DataTable
                header={renderRepromaterialHeader}
                value={repromaterialOverview}
                selectionMode="single"
                selection={selectedProduct}
                onSelectionChange={(e) => {
                  setSelectedProductList([]);
                  setSelectedProduct(e.value);
                }}
                onRowSelect={() => setListOfProductsVisible(true)}
                dataKey="code"
                metaKeySelection={false}
                showGridlines
                stripedRows
              >
                <Column field="code" header={`${t("Šifra robe")}`} />
                <Column field="name" header={`${t("Naziv proizvoda")}`} />
                <Column body={typeOfProduct} header={`${t("Tip robe")}`} />
                <Column
                  body={categoryOfProduct}
                  header={`${t("Vrsta robe")}`}
                />
                <Column body={markOfProduct} header={`${t("Oznaka")}`} />
                <Column body={amountOfProduct} header={`${t("Količina")}`} />
              </DataTable>
            </div>
          )}
      </div>
      <ListOfProducts
        visible={listOfProductsVisible}
        productsOverview={selectedProductList}
        buttonInactiveStyle={{
          border: "none",
          background: "rgba(108, 117, 125, 1)",
        }}
        handleClose={() => {
          setListOfProductsVisible(false);
          setSelectedProductList([]);
        }}
      />
    </PageLayout>
  );
};
